import React, { useState, useCallback } from 'react';
import { X, Check, RotateCcw } from 'lucide-react';

const SimpleInstructionPlayer = ({ config, taskTitle, instructions, onComplete, onClose }) => {
  const color = config?.color || '#22D3EE';
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [checked, setChecked] = useState(new Set());

  const items = instructions || [];
  const allChecked = items.length > 0 && checked.size === items.length;

  const toggleItem = (idx) => {
    setChecked(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  };

  const handleComplete = () => setFinished(true);
  const handleReset = () => { setChecked(new Set()); setStarted(false); setFinished(false); };

  const handleFinish = useCallback(() => {
    onComplete({
      started: true, checklist_completed: checked.size, checklist_total: items.length, task_type: 'simple_instruction',
    });
  }, [checked.size, items.length, onComplete]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="instruction-player">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-24 h-24 rounded-2xl flex items-center justify-center" style={{
            background: `${color}15`, border: `1px solid ${color}25`, boxShadow: `0 0 40px ${color}15`,
          }}>
            <svg className="w-12 h-12" style={{ color }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 14l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm">{items.length} adım — adım adım tamamla</p>
          </div>
          <button onClick={() => setStarted(true)}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="instruction-start">
            Başla
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Görev Tamamlandı!</h2>
            <p className="text-white/40 text-sm">{checked.size} / {items.length} adım tamamlandı</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="instruction-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full max-w-md gap-5 mt-8">
          <div className="text-white/20 text-xs tracking-widest">{checked.size} / {items.length} tamamlandı</div>

          <div className="w-full space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {items.map((item, i) => {
              const done = checked.has(i);
              return (
                <button key={i} onClick={() => toggleItem(i)}
                  className="w-full flex items-start gap-3 p-4 rounded-xl text-left transition-all hover:scale-[1.01]"
                  style={{
                    background: done ? `${color}10` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${done ? `${color}30` : 'rgba(255,255,255,0.05)'}`,
                  }} data-testid={`checklist-item-${i}`}>
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-all" style={{
                    background: done ? color : 'rgba(255,255,255,0.05)',
                    border: `2px solid ${done ? color : 'rgba(255,255,255,0.15)'}`,
                  }}>
                    {done && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm leading-relaxed transition-colors" style={{
                    color: done ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.7)',
                    textDecoration: done ? 'line-through' : 'none',
                  }}>{item}</span>
                </button>
              );
            })}
          </div>

          <button onClick={handleComplete} disabled={checked.size === 0}
            className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.01] disabled:opacity-30 disabled:hover:scale-100"
            style={{
              background: allChecked ? 'linear-gradient(135deg, #34D399, #34D399BB)' : `linear-gradient(135deg, ${color}, ${color}BB)`,
              boxShadow: allChecked ? '0 8px 30px rgba(52,211,153,0.3)' : `0 8px 30px ${color}30`,
            }} data-testid="instruction-complete">
            {allChecked ? 'Tümü Tamamlandı!' : `Tamamla (${checked.size}/${items.length})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default SimpleInstructionPlayer;
